/**
 * Tenant Request API Service
 * Provides methods for managing tenant requests from org owners.
 * All operations require authentication.
 */

import { ApiService } from './api';
import type {
  TenantRequest,
  TenantRequestCreate,
  TenantRequestReject,
  TenantRequestStatus,
  PaginatedTenantRequests,
  TenantRegistrationRequest,
  FulfillmentChecklist,
  StatusChangeRequestCreate,
} from '../types/tenantRequest';

/**
 * Tenant Request API Client
 */
class TenantRequestApiServiceClass {
  /**
   * Get base URL for tenant request endpoints.
   */
  private getBaseUrl(): string {
    return '/api/tenant-requests';
  }

  /**
   * List tenant requests with optional filtering and pagination.
   *
   * Global admins see all requests across organizations.
   * Organization owners/admins only see their organization's requests.
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of tenant requests
   */
  async listRequests(params?: {
    organization_id?: number;
    status?: TenantRequestStatus;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedTenantRequests> {
    const queryParams = new URLSearchParams();

    if (params?.organization_id) {
      queryParams.append('organization_id', params.organization_id.toString());
    }
    if (params?.status) {
      queryParams.append('status_filter', params.status);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}?${queryParams.toString()}`
      : this.getBaseUrl();

    const response = await ApiService.request<PaginatedTenantRequests>(url, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Get tenant request details by ID.
   *
   * Global admins can view any request.
   * Organization users can only view their organization's requests.
   *
   * @param requestId - Request ID
   * @returns Tenant request details
   * @throws Error if request not found or access denied
   */
  async getRequest(requestId: number): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}`,
      {
        method: 'GET',
      }
    );
    return response;
  }

  /**
   * Create a new tenant request for the user's organization.
   *
   * Requires organization_owner role or organization:tenants:request permission.
   * User must be assigned to an organization (organization_id from auth context).
   *
   * @param data - Tenant request data (name, notes)
   * @returns Created tenant request details
   * @throws Error if user not assigned to org or lacks permission
   */
  async createRequest(data: TenantRequestCreate): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      this.getBaseUrl(),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    return response;
  }

  /**
   * Create a status change request for a tenant in the user's organization.
   *
   * Requires organization_owner role or organization:tenants:request permission.
   * User must be assigned to an organization.
   * Target tenant must belong to the user's organization.
   *
   * @param data - Status change request data (target_tenant_id, requested_status, notes)
   * @returns Created tenant request details
   * @throws Error if user not assigned to org, lacks permission, or tenant not in org
   */
  async createStatusChangeRequest(data: StatusChangeRequestCreate): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/status-change`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    return response;
  }

  /**
   * Approve a tenant request.
   *
   * Only global admins can approve requests.
   *
   * @param requestId - Request ID to approve
   * @returns Updated tenant request
   * @throws Error if user is not global admin or request not found
   */
  async approveRequest(requestId: number): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/approve`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * Reject a tenant request.
   *
   * Only global admins can reject requests.
   *
   * @param requestId - Request ID to reject
   * @param data - Rejection data (optional reason)
   * @returns Updated tenant request
   * @throws Error if user is not global admin or request not found
   */
  async rejectRequest(
    requestId: number,
    data: TenantRequestReject
  ): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/reject`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
    return response;
  }

  /**
   * Start fulfillment process for a tenant request.
   *
   * Changes status from pending to in_process and initializes checklist.
   * Only global admins can start fulfillment.
   *
   * @param requestId - Request ID to start fulfillment for
   * @returns Updated tenant request with in_process status
   * @throws Error if user is not global admin or request not found
   */
  async startFulfillment(requestId: number): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/start-fulfillment`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * Complete fulfillment by creating tenant and marking request fulfilled.
   *
   * Creates a new managed tenant record and links it to the request.
   * Changes status from in_process to fulfilled.
   * Only global admins can complete fulfillment.
   *
   * @param requestId - Request ID to complete fulfillment for
   * @param tenantData - Tenant registration data (tenant_id, name, domain)
   * @returns Updated tenant request with fulfilled status
   * @throws Error if user is not global admin or request not found
   */
  async completeFulfillment(
    requestId: number,
    tenantData: TenantRegistrationRequest
  ): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/complete-fulfillment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tenantData),
      }
    );
    return response;
  }

  /**
   * Update the fulfillment checklist for a tenant request.
   *
   * Allows saving progress on the fulfillment checklist during
   * the in_process state. Only global admins can update the checklist.
   *
   * @param requestId - Request ID to update checklist for
   * @param checklist - Checklist values to save (merges with existing)
   * @returns Updated tenant request with new checklist values
   * @throws Error if user is not global admin or request not found
   */
  async updateChecklist(
    requestId: number,
    checklist: FulfillmentChecklist
  ): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/checklist`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checklist),
      }
    );
    return response;
  }

  /**
   * Link an existing managed tenant to a request and mark as fulfilled.
   *
   * Used when a tenant is registered via the standard registration flow
   * but needs to be linked to a pending tenant request.
   *
   * @param requestId - Request ID to fulfill
   * @param tenantDbId - Database ID of the existing managed tenant
   * @returns Updated tenant request with fulfilled status
   * @throws Error if user is not global admin or request/tenant not found
   */
  async linkTenantToRequest(
    requestId: number,
    tenantDbId: number
  ): Promise<TenantRequest> {
    const response = await ApiService.request<TenantRequest>(
      `${this.getBaseUrl()}/${requestId}/link-tenant?tenant_db_id=${tenantDbId}`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * Delete a tenant request.
   *
   * Only allows deletion of requests that are not fulfilled.
   * Global admins can delete any request.
   * Org owners can only delete requests from their own organization.
   *
   * @param requestId - Request ID to delete
   * @returns Success response
   * @throws Error if request not found, already fulfilled, or access denied
   */
  async deleteRequest(requestId: number): Promise<{ success: boolean; message: string }> {
    const response = await ApiService.request<{ success: boolean; message: string }>(
      `${this.getBaseUrl()}/${requestId}`,
      {
        method: 'DELETE',
      }
    );
    return response;
  }
}

// Export singleton instance
export const TenantRequestApiService = new TenantRequestApiServiceClass();
